import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks";

const STEPS = [
  { at: 0,  label: "Schema setup queued…" },
  { at: 10, label: "Creating database schema…" },
  { at: 30, label: "Running migrations…" },
  { at: 70, label: "Setting up scheduled tasks…" },
  { at: 90, label: "Syncing user data…" },
  { at: 100, label: "Setup complete!" },
];

const WorkspaceSetup: React.FC = () => {
  const navigate = useNavigate();
  const { schemaSetup } = useAuth();
  const { progress, status, message, usedFallback } = schemaSetup;

  useEffect(() => {
    if (status === "completed") {
      navigate("/pricing");
    }
  }, [status, navigate]);

  const displayMessage =
    message ||
    STEPS.slice()
      .reverse()
      .find((s) => progress >= s.at)?.label ||
    "Setting up your workspace…";

  const isFailed = status === "failed";

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(160deg,#eef2fc 0%,#f5f7fd 40%,#edf1fb 70%,#f2f5fd 100%)",
        padding: "24px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Atmospheric blobs */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(ellipse 700px 500px at -8% -8%,  rgba(26,61,124,0.10) 0%, transparent 52%),
            radial-gradient(ellipse 600px 480px at 108% 8%,  rgba(42,91,168,0.07) 0%, transparent 52%),
            radial-gradient(ellipse 500px 380px at 52% 110%, rgba(48,205,115,0.07) 0%, transparent 52%)
          `,
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "480px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "32px",
        }}
      >
        {/* Logo / brand mark */}
        <div
          style={{
            width: "64px",
            height: "64px",
            borderRadius: "18px",
            background: "linear-gradient(135deg, #1a3d7c 0%, #2a5ba8 55%, #30cd73 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 32px rgba(26,61,124,0.25)",
          }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Heading */}
        <div style={{ textAlign: "center" }}>
          <h1
            style={{
              fontSize: "clamp(22px,4vw,32px)",
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.025em",
              marginBottom: "10px",
              lineHeight: 1.15,
            }}
          >
            {isFailed ? "Setup failed" : "Setting up your workspace"}
          </h1>
          <p
            style={{
              fontSize: "14px",
              color: "#475569",
              lineHeight: 1.65,
              maxWidth: "360px",
              margin: "0 auto",
            }}
          >
            {isFailed
              ? "Something went wrong during setup. Please contact support."
              : "We're preparing everything for you. This usually takes less than a minute."}
          </p>
        </div>

        {isFailed ? (
          /* ── Error state ── */
          <div
            style={{
              width: "100%",
              padding: "24px",
              borderRadius: "16px",
              background: "rgba(254,242,242,0.9)",
              border: "1.5px solid rgba(239,68,68,0.25)",
              textAlign: "center",
            }}
          >
            <p style={{ color: "#b91c1c", fontWeight: 600, marginBottom: "16px" }}>
              {message || "An unexpected error occurred."}
            </p>
            <a
              href="mailto:support@apprevbooster.com"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "10px 22px",
                borderRadius: "10px",
                background: "linear-gradient(135deg,#1a3d7c,#2a5ba8)",
                color: "#fff",
                fontWeight: 700,
                fontSize: "13px",
                textDecoration: "none",
              }}
            >
              Contact Support
            </a>
          </div>
        ) : (
          /* ── Progress card ── */
          <div
            style={{
              width: "100%",
              padding: "28px 28px 24px",
              borderRadius: "20px",
              background: "rgba(255,255,255,0.88)",
              border: "1.5px solid rgba(26,61,124,0.10)",
              boxShadow: "0 4px 24px rgba(26,61,124,0.08)",
              backdropFilter: "blur(8px)",
            }}
          >
            {/* Progress percentage */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: "10px",
              }}
            >
              <span
                style={{ fontSize: "13px", fontWeight: 600, color: "#1a3d7c" }}
              >
                {displayMessage}
              </span>
              <span
                style={{ fontSize: "13px", fontWeight: 700, color: "#1a3d7c" }}
              >
                {progress}%
              </span>
            </div>

            {/* Progress bar track */}
            <div
              style={{
                height: "8px",
                borderRadius: "99px",
                background: "rgba(26,61,124,0.10)",
                overflow: "hidden",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: "99px",
                  background:
                    "linear-gradient(90deg, #1a3d7c 0%, #2a5ba8 50%, #30cd73 100%)",
                  width: `${progress}%`,
                  transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
            </div>

            {/* Step dots */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "14px",
              }}
            >
              {STEPS.map((step) => (
                <div
                  key={step.at}
                  title={step.label}
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background:
                      progress >= step.at
                        ? "linear-gradient(135deg, #1a3d7c, #30cd73)"
                        : "rgba(26,61,124,0.15)",
                    transition: "background 0.4s ease",
                    flexShrink: 0,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Fallback indicator */}
        {usedFallback && !isFailed && (
          <p
            style={{
              fontSize: "11px",
              color: "#94a3b8",
              textAlign: "center",
            }}
          >
            Connecting via fallback mode…
          </p>
        )}
      </div>

      <style>{`
        @keyframes ws-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default WorkspaceSetup;
